import {
  Archive,
  ArchiveRestore,
  CopyPlus,
  EditIcon,
  Trash2,
} from 'lucide-react'
import type { ReactNode } from 'react'

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui'
import type { CellContext } from '@/components/ui/table/types'
import { allowsManyProjects, getAccount } from '@/modules/account'
import { DeleteProjectsDialog } from '@/modules/projects/components/dialogs/DeleteProjectsDialog'
import { EditProjectDialog } from '@/modules/projects/components/dialogs/EditProjectDialog'
import { isProjectDeletable } from '@/modules/projects/expiration'
import { isProjectBusy } from '@/modules/projects/status'
import type { Project } from '@/modules/projects/stores/types'
import { getDialogActions } from '@/stores/dialog'
import { archiveProjects, unarchiveProjects } from './archiveProjects'
import { duplicateProject } from './duplicateProject'

export const ContextMenuCell = <T extends string | number>(
  Cell?: (cell: CellContext<Project, T>) => ReactNode,
) => {
  const { openDialog } = getDialogActions()

  return (cell: CellContext<Project, T>) => {
    const project = cell.row.original as Project
    const value = Cell ? Cell(cell) : cell.getValue()
    const account = getAccount()

    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className="h-full w-full flex items-center cursor-context-menu">
            {value}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          {project.archivedAt ? (
            <ContextMenuItem onClick={() => unarchiveProjects([project])}>
              <ArchiveRestore className="size-4" />
              Unarchive project
            </ContextMenuItem>
          ) : (
            <>
              <ContextMenuItem
                onClick={() => openDialog(EditProjectDialog.id, project)}
              >
                <EditIcon className="size-4" />
                Edit project
              </ContextMenuItem>
              {allowsManyProjects(account?.planKind) && (
                <ContextMenuItem
                  onClick={() => duplicateProject(project.slug)}
                  disabled={isProjectBusy(project)}
                >
                  <CopyPlus className="size-4" />
                  Duplicate project
                </ContextMenuItem>
              )}
              <ContextMenuItem
                onClick={() => archiveProjects([project])}
                disabled={isProjectBusy(project)}
              >
                <Archive className="size-4" />
                Archive project
              </ContextMenuItem>
            </>
          )}
          {isProjectDeletable(project, account?.planKind) && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => openDialog(DeleteProjectsDialog.id, [project])}
                destructive
                disabled={isProjectBusy(project)}
              >
                <Trash2 className="size-4" />
                Delete project
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>
    )
  }
}
